import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Shield, Sparkles, TrendingUp, X } from 'lucide-react';
import { addNote } from '@/lib/api/tickets.js';
import type { PredictiveAlert } from '@/lib/api/tickets.js';
import { dismissPredictiveAlerts } from '@/lib/predictive-alerts-storage.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

const severityStyles: Record<PredictiveAlert['severity'], string> = {
  high: 'border-red-200 bg-red-50 text-red-950',
  medium: 'border-amber-200 bg-amber-50 text-amber-950',
  low: 'border-blue-200 bg-blue-50 text-blue-950',
};

function AlertIcon({ type }: { type: string }) {
  if (type === 'repeat_repair') return <AlertTriangle className="h-5 w-5 shrink-0 text-red-600" />;
  if (type === 'repair_vs_value') return <TrendingUp className="h-5 w-5 shrink-0 text-amber-600" />;
  if (type === 'high_value_customer') return <Sparkles className="h-5 w-5 shrink-0 text-primary" />;
  return <Shield className="h-5 w-5 shrink-0 text-primary" />;
}

export function PredictiveAlertsBanner({
  ticketId,
  alerts,
  onDismiss,
  onNoteAdded,
}: {
  ticketId: string;
  alerts: PredictiveAlert[];
  onDismiss: () => void;
  onNoteAdded?: () => void;
}) {
  const noteMut = useMutation({
    mutationFn: (alert: PredictiveAlert) =>
      addNote(ticketId, {
        body: `[Predictive Alert — ${alert.title}]\n${alert.message}\n\nSuggestion: ${alert.suggestion}`,
        visibility: 'private',
        pinned: false,
      }),
    onSuccess: () => {
      toastSuccess('Protection note added');
      onNoteAdded?.();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (!alerts.length) return null;

  function handleDismiss() {
    dismissPredictiveAlerts(ticketId);
    onDismiss();
  }

  return (
    <section className="space-y-3">
      {alerts.map((alert) => (
        <div
          key={alert.type}
          className={cn(
            'rounded-2xl border p-4 shadow-sm ring-1 ring-inset ring-black/5',
            severityStyles[alert.severity],
          )}
        >
          <div className="flex items-start gap-3">
            <AlertIcon type={alert.type} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold">{alert.title}</h3>
                <button
                  type="button"
                  onClick={handleDismiss}
                  className="rounded-lg p-1 opacity-60 hover:bg-black/5 hover:opacity-100"
                  aria-label="Dismiss alerts"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-1 text-sm">{alert.message}</p>
              <p className="mt-2 text-sm font-medium">{alert.suggestion}</p>
              {alert.action === 'suggest_protection' || alert.type === 'repeat_repair' ? (
                <button
                  type="button"
                  disabled={noteMut.isPending}
                  onClick={() => noteMut.mutate(alert)}
                  className="mt-3 rounded-xl bg-primary px-3 py-1.5 text-xs font-bold text-white hover:opacity-95 disabled:opacity-50"
                >
                  Add Protection Note
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}
